import { useState } from "react";
import { Modal } from "@/components/base/Modal";
import { Button } from "@/components/base/Button";
import { usersService } from "@/services";
import type { StaffUser } from "@/services/types";
import { useToast } from "@/context/ToastContext";

interface PasswordResetModalProps {
  open: boolean;
  onClose: () => void;
  user: StaffUser | null;
  actor: string;
}

export function PasswordResetModal({ open, onClose, user, actor }: PasswordResetModalProps) {
  const toast = useToast();
  const [generated, setGenerated] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const close = () => {
    setGenerated(null);
    onClose();
  };

  const generate = async () => {
    if (!user || loading) return;
    setLoading(true);
    try {
      const result = await usersService.resetUserPassword(user.id, actor);
      setGenerated(result.temporaryPassword);
      toast.info("Password reset", "A temporary password was generated. It is shown only once.");
    } catch (err) {
      toast.error("Could not reset password", err instanceof Error ? err.message : "Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const copy = async () => {
    if (!generated) return;
    try {
      await navigator.clipboard.writeText(generated);
      toast.success("Copied", "Temporary password copied to clipboard.");
    } catch {
      toast.error("Copy failed", "Select and copy the password manually.");
    }
  };

  return (
    <Modal
      open={open}
      onClose={close}
      title="Reset password"
      description={user ? `${user.name} · ${user.email}` : ""}
      hideCloseButton
    >
      {!generated ? (
        <div className="space-y-4">
          <div className="rounded-md bg-background-100/60 px-3 py-2.5 text-xs text-foreground-600">
            A random temporary password will be generated and shown once. Share it with the staff member directly — it
            is not stored and cannot be shown again. They should change it after first sign-in.
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={close} disabled={loading}>
              Cancel
            </Button>
            <Button icon={<i className="ri-key-line" />} loading={loading} onClick={generate}>
              Generate temporary password
            </Button>
          </div>
        </div>
      ) : (
        <div className="space-y-4">
          <div className="rounded-md border border-background-200 bg-background-50 p-4 text-center">
            <p className="text-xs text-foreground-500">Temporary password</p>
            <p className="tnum mt-2 select-all font-mono text-2xl font-semibold tracking-wide text-foreground-950">
              {generated}
            </p>
            <p className="mt-2 text-xs text-danger-600">Shown once — copy it now.</p>
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={close}>
              Done
            </Button>
            <Button icon={<i className="ri-clipboard-line" />} onClick={copy}>
              Copy password
            </Button>
          </div>
        </div>
      )}
    </Modal>
  );
}